import { z } from "zod";
import { isoDate, uuid } from "./common";
import type { Permission } from "./permissions";

export const REPORTS = {
  appointments: { title: "Appointments by doctor", group: "Clinic", permission: "reports.view" },
  new_patients: { title: "New patients by source", group: "Clinic", permission: "reports.view" },
  recalls: { title: "Recalls due", group: "Clinic", permission: "reports.view" },
  leads: { title: "Leads by source", group: "Marketing", permission: "reports.view" },
  campaigns: { title: "Campaign results", group: "Marketing", permission: "reports.view" },
  revenue_by_doctor: { title: "Invoiced by doctor", group: "Money", permission: "reports.financial" },
  revenue_by_service: { title: "Invoiced by treatment", group: "Money", permission: "reports.financial" },
  collections: { title: "Money received by method", group: "Money", permission: "reports.financial" },
  treatment_conversion: { title: "Treatment plan conversion", group: "Money", permission: "reports.financial" },
  insurance: { title: "Insurance claims by payer", group: "Money", permission: "reports.financial" },
  inventory_usage: { title: "Stock used", group: "Operations", permission: "inventory.view" },
  lab_cases: { title: "Lab work by lab", group: "Operations", permission: "labs.manage" },
  ai_usage: { title: "AI assistant usage", group: "Administration", permission: "settings.manage" },
  therapy_daily: { title: "Therapy sessions per day", group: "Therapy", permission: "therapy.view" },
  therapist_workload: { title: "Therapist workload", group: "Therapy", permission: "therapy.view" },
  room_use: { title: "Therapy table use", group: "Therapy", permission: "therapy.view" },
  oil_used: { title: "Oils and materials used in therapy", group: "Therapy", permission: "inventory.view" },
  top_conditions: { title: "Most common conditions", group: "Clinic", permission: "reports.view" },
  packages_sold: { title: "Packages sold", group: "Money", permission: "reports.financial" },
} as const satisfies Record<string, { title: string; group: string; permission: Permission }>;
export type ReportKey = keyof typeof REPORTS;

export const reportQuery = z
  .object({
    from: isoDate,
    to: isoDate,
    branchId: uuid.optional(),
    format: z.enum(["json", "csv"]).default("json"),
  })
  .refine((q) => q.to >= q.from, { path: ["to"], message: "The end is before the start" });

export type ReportColumnKind = "text" | "number" | "money" | "percent";

export interface ReportResult {
  key: ReportKey;
  title: string;
  /** Plain-language note on what is counted (e.g. "before credit notes"). */
  note: string | null;
  from: string;
  to: string;
  currency: string;
  columns: { key: string; label: string; kind: ReportColumnKind }[];
  rows: Record<string, string | number | null>[];
  totals: Record<string, number | null> | null;
}
